// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import { XNGAGE_CUSTOM_WIDGET } from "@west/XDL/constants";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import parse from "html-react-parser";
import * as React from "react";
import { shallowEqual } from "react-redux";

const enum fields {
    image = "image",
    mobileSpecificImage = "mobileSpecificImage",
    title = "title",
    description = "description",
    buttonLabel = "buttonLabel",
    link = "link",
    openInNewTab = "openInNewTab",
    buttonPositionAlignment = "buttonPositionAlignment",
}

interface InfoCardItemProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.mobileSpecificImage]?: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.buttonLabel]: string;
        [fields.link]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.buttonPositionAlignment]: "flex-end" | "center" | "flex-start";
    };
}

interface ButtonContainerProps extends InjectableCss {
    buttonPosition?: string;
}

interface InfoCardStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    description?: TypographyProps;
    image?: LazyImageProps;
    buttonContainer?: ButtonContainerProps;
    button?: ButtonPresentationProps;
}

export const infoCardStyles: InfoCardStyles = {};

const styles = infoCardStyles;
const InfoCard: React.FC<InfoCardItemProps> = ({ fields }) => {
    const { xGoogleAnalytics } = useAppSelector(state => {
        return {
            xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
        };
    }, shallowEqual);
    const url = useGetLink(fields.link).url;

    const googleAnalyticsClickHandler = () => {
        if (xGoogleAnalytics.hpFooterWidgetEvent && url) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.hpFooterWidgetEventName,
                click_text: fields.buttonLabel,
                destination: url,
                affiliation: xGoogleAnalytics.affiliation,
                authentication_state: xGoogleAnalytics.authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };
    return (
        <StyledWrapper {...styles.container} data-test-selector="info_card">
            <LazyImage src={fields.image} altText={fields.title} {...styles.image} />
            <Typography data-test-selector="info_card_title" {...styles.title}>
                {fields.title}
            </Typography>
            <Typography data-test-selector="info_card_description" {...styles.description}>
                {parse(fields.description, parserOptions)}
            </Typography>
            {fields.buttonLabel && (
                <StyledWrapper buttonPosition={fields.buttonPositionAlignment} {...styles.buttonContainer}>
                    <Clickable
                        href={url}
                        target={fields.openInNewTab ? "_blank" : undefined}
                        onClick={googleAnalyticsClickHandler}
                    >
                        <Button {...styles.button} aria-label={fields.buttonLabel}>
                            {fields.buttonLabel}
                        </Button>
                    </Clickable>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCard,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                name: fields.image,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                isRequired: false,
                fieldType: "General",
            },
            {
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { value: "", type: "Url" },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                variant: "toggle",
                fieldType: "General",
            },
            {
                name: fields.buttonPositionAlignment,
                displayName: "Button Position Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Right", value: "flex-end" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Left", value: "flex-start" },
                ],
                hideEmptyOption: true,
                defaultValue: "flex-end",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
